import React, { useState, useMemo } from 'react';
import Papa from 'papaparse';
import { motion, AnimatePresence } from 'framer-motion';
import UploadZone from './components/UploadZone';
import KeywordCloud from './components/KeywordCloud';
import InsightPanel from './components/InsightPanel';
import HighValueIntent from './components/HighValueIntent';
import TrafficStrategy from './components/TrafficStrategy';
import CPCAnalysis from './components/CPCAnalysis';
import EducationalSection from './components/EducationalSection';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { detectColumns, analyzeData, generateInsights } from './utils/analyzer';
import { cn } from './utils/cn';

function App() {
  const [dataState, setDataState] = useState({
    raw: null,
    columnMap: null,
    isProcessing: false
  });
  
  const [filters, setFilters] = useState({
    length: 'all',
    region: 'Global'
  });

  const [selectedToken, setSelectedToken] = useState(null);

  const handleFileProcess = (file) => {
    setDataState(prev => ({ ...prev, isProcessing: true }));
    
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const headers = results.meta.fields;
        const columnMap = detectColumns(headers);
        
        if (!columnMap.keyword || !columnMap.volume) {
          alert('Missing essential headers. Ensure "Keyword" and "Volume" are present.');
          setDataState(prev => ({ ...prev, isProcessing: false }));
          return;
        }

        setDataState({
          raw: results.data,
          columnMap,
          isProcessing: false
        });
      },
      error: () => {
        alert('Error parsing CSV');
        setDataState(prev => ({ ...prev, isProcessing: false }));
      }
    });
  };

  const processedData = useMemo(() => {
    if (!dataState.raw) return null;

    const filteredRaw = dataState.raw.filter(row => {
      const kw = row[dataState.columnMap.keyword]?.toString().trim() || '';
      const wordCount = kw.split(/\s+/).filter(w => w.length > 0).length;

      if (filters.length === '1') return wordCount === 1;
      if (filters.length === '2') return wordCount === 2;
      if (filters.length === '3+') return wordCount >= 3;
      return true;
    });

    const analysis = analyzeData(filteredRaw, dataState.columnMap);
    const insights = generateInsights(analysis);

    return { ...analysis, insights };
  }, [dataState.raw, dataState.columnMap, filters.length]);

  const handleReset = () => {
    setDataState({ raw: null, columnMap: null, isProcessing: false });
    setSelectedToken(null);
    setFilters({ length: 'all', region: 'Global' });
  };

  return (
    <div className="min-h-screen bg-[#fcfcfd] dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans selection:bg-yellow-200">
      <header className="sticky top-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/60 dark:border-slate-800/60">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <div className="bg-yellow-500 text-slate-900 p-2 sm:p-2.5 rounded-xl sm:rounded-2xl shadow-lg shadow-yellow-500/20">
              <SafeIcon icon={FiIcons.FiWind} className="w-4 h-4 sm:w-5 h-5" />
            </div>
            <div>
              <h1 className="text-sm sm:text-xl font-black tracking-tight leading-none uppercase">
                Keyword <span className="text-yellow-600 dark:text-yellow-500">Intel Pro</span>
              </h1>
              <span className="hidden xs:block text-[8px] sm:text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Semantic Analysis Engine</span>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-4 overflow-x-auto no-scrollbar">
            {dataState.raw && (
              <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700 shrink-0">
                {['all', '1', '2', '3+'].map((id) => (
                  <button
                    key={id}
                    onClick={() => {
                      setFilters(f => ({ ...f, length: id }));
                      setSelectedToken(null);
                    }}
                    className={cn(
                      "px-2 sm:px-3 py-1 sm:py-1.5 text-[9px] sm:text-[10px] font-black uppercase tracking-wider rounded-lg transition-all whitespace-nowrap",
                      filters.length === id 
                        ? "bg-white dark:bg-slate-700 text-yellow-600 shadow-sm" 
                        : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                    )}
                  >
                    {id === 'all' ? 'All' : id + (id === '3+' ? ' Words' : ' Word')}
                  </button>
                ))}
              </div>
            )}
            
            <div className="flex items-center gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shrink-0">
              <SafeIcon icon={FiIcons.FiGlobe} className="w-3 h-3 sm:w-3.5 h-3.5 text-slate-400" />
              <select 
                value={filters.region}
                onChange={(e) => setFilters(f => ({ ...f, region: e.target.value }))}
                className="bg-transparent text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-slate-600 dark:text-slate-300 focus:outline-none cursor-pointer"
              >
                <option value="Global">Global</option>
                <option value="US">US</option>
                <option value="UK">UK</option>
                <option value="EU">EU</option>
              </select>
            </div>

            {dataState.raw && (
              <button onClick={handleReset} className="p-2 sm:p-2.5 bg-slate-100 dark:bg-slate-800 hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-500 hover:text-red-600 rounded-xl transition-all shrink-0">
                <SafeIcon icon={FiIcons.FiRefreshCw} className="w-3.5 h-3.5 sm:w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 sm:py-12">
        {!dataState.raw ? (
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12 sm:mb-20">
              <h2 className="text-4xl sm:text-6xl lg:text-7xl font-black text-slate-900 dark:text-white mb-6 sm:mb-8 tracking-tighter leading-[1.1]">
                Master Your <span className="text-yellow-500">Semantic</span> <br className="hidden sm:block" />
                Search Strategy.
              </h2>
              <p className="text-slate-500 dark:text-slate-400 text-base sm:text-xl mb-8 sm:mb-12 max-w-2xl mx-auto font-medium leading-relaxed px-4">
                The enterprise-grade semantic clustering engine. Transform raw data into high-intent content hubs and local search dominance.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-12 sm:mb-16">
                <div className="flex -space-x-3">
                  {[1,2,3,4].map(i => (
                    <img key={i} src={`https://i.pravatar.cc/100?img=${i+10}`} className="w-8 h-8 sm:w-10 h-10 rounded-full border-2 border-white dark:border-slate-900 shadow-sm" alt="User" />
                  ))}
                </div>
                <div className="text-center sm:text-left">
                  <div className="flex justify-center sm:justify-start text-yellow-500 mb-0.5">
                    {[1,2,3,4,5].map(i => <SafeIcon key={i} icon={FiIcons.FiStar} className="w-3 h-3 fill-current" />)}
                  </div>
                  <p className="text-[10px] sm:text-[11px] font-bold text-slate-400 uppercase tracking-widest">Trusted by 2,000+ SEO Pros</p>
                </div>
              </div>
              <div className="px-4">
                <UploadZone onFileProcess={handleFileProcess} />
              </div>
            </motion.div>
            
            <EducationalSection />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-10 pb-20">
            {/* Main Content Area */}
            <div className="lg:col-span-8 flex flex-col gap-8 sm:gap-10 order-2 lg:order-1">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
                {[
                  { label: 'Keywords', val: processedData.stats.totalKeywords, color: 'text-slate-900 dark:text-white' },
                  { label: 'Volume', val: processedData.stats.totalVolume, color: 'text-yellow-600 dark:text-yellow-500' },
                  { label: 'Value', val: `$${processedData.stats.totalTrafficValue.toLocaleString()}`, color: 'text-emerald-500' },
                  { label: 'Rank', val: processedData.stats.avgPosition.toFixed(1), color: 'text-amber-500' }
                ].map((stat, i) => (
                  <div key={i} className="bg-white dark:bg-slate-900 p-5 sm:p-8 rounded-2xl sm:rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm">
                    <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-1 sm:mb-2">{stat.label}</div>
                    <div className={cn("text-xl sm:text-3xl font-black truncate", stat.color)}>{stat.val.toLocaleString()}</div>
                  </div>
                ))}
              </div>

              <div className="h-[400px] sm:h-[600px]">
                <KeywordCloud 
                  tokens={processedData.tokens} 
                  stats={processedData.stats}
                  selectedToken={selectedToken}
                  onTokenClick={setSelectedToken}
                />
              </div>

              <div className="space-y-8 sm:space-y-10">
                <HighValueIntent keywords={processedData.insights.highValueIntent} />
                <TrafficStrategy gaps={processedData.insights.trafficGaps} />
                <CPCAnalysis leaders={processedData.insights.cpcLeaders} />
              </div>

              <AnimatePresence>
                {selectedToken && (
                  <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }} className="bg-white dark:bg-slate-900 p-6 sm:p-10 rounded-2xl sm:rounded-3xl border-2 border-yellow-500/20 shadow-2xl shadow-yellow-500/10">
                    <div className="flex justify-between items-center mb-6 sm:mb-8">
                      <h4 className="font-black text-lg sm:text-2xl capitalize flex items-center gap-2 sm:gap-3">
                        <span className="text-slate-400">Cluster:</span>
                        <span className="text-yellow-600 underline decoration-yellow-200 decoration-2 sm:decoration-4">{selectedToken.token}</span>
                      </h4>
                      <button onClick={() => setSelectedToken(null)} className="p-2 sm:p-3 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl sm:rounded-2xl transition-all"><SafeIcon icon={FiIcons.FiX} className="w-4 h-4 sm:w-5 h-5" /></button>
                    </div>
                    <div className="flex flex-wrap gap-3 max-h-48 sm:max-h-60 overflow-y-auto custom-scrollbar pr-2 sm:pr-4">
                      {processedData.keywords
                        .filter(k => k.keyword.includes(selectedToken.token))
                        .map((kw, i) => (
                          <div key={i} className="px-4 py-2 bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50 rounded-xl text-[10px] sm:text-xs font-bold flex items-center gap-3 whitespace-nowrap">
                            <span className="text-slate-800 dark:text-slate-200">{kw.keyword}</span>
                            <span className="text-[9px] sm:text-[10px] text-yellow-600 bg-yellow-50 dark:bg-yellow-900/30 px-1.5 py-0.5 rounded font-black">#{kw.position}</span>
                          </div>
                        ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Sidebar Section */}
            <aside className="lg:col-span-4 order-1 lg:order-2">
              <div className="lg:sticky lg:top-24">
                <InsightPanel insights={processedData.insights} />
              </div>
            </aside>
          </div>
        )}
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-800 py-8 sm:py-12 bg-white dark:bg-slate-900 mt-20">
        <div className="max-w-[1600px] mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-3">
            <div className="bg-yellow-500 text-slate-900 p-2 rounded-lg">
              <SafeIcon icon={FiIcons.FiWind} className="w-4 h-4" />
            </div>
            <p className="text-sm font-black text-slate-800 dark:text-white uppercase tracking-widest">
              © 2026 sheker.com
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-6 sm:gap-8 text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-slate-400">
            <a href="#" className="hover:text-yellow-600 transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-yellow-600 transition-colors">Terms of Service</a>
            <a href="#" className="hover:text-yellow-600 transition-colors">Documentation</a>
          </div>
          <div className="flex items-center gap-6 text-slate-400">
            <SafeIcon icon={FiIcons.FiTwitter} className="w-5 h-5 hover:text-yellow-500 cursor-pointer transition-colors" />
            <SafeIcon icon={FiIcons.FiGithub} className="w-5 h-5 hover:text-slate-900 dark:hover:text-white cursor-pointer transition-colors" />
            <SafeIcon icon={FiIcons.FiLinkedin} className="w-5 h-5 hover:text-yellow-700 cursor-pointer transition-colors" />
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;